import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';

/**
 * Props for TeamCodesDisplay component
 */
interface TeamCodesDisplayProps {
    coachCode: string;
    athleteCode: string;
    teamName: string;
    organizationName: string;
}

/**
 * Team codes display component
 * 
 * Displays coach and athlete invite codes after successful team creation.
 * Includes copy-to-clipboard functionality with visual feedback.
 * Theme-aware styling matching existing CGM design patterns.
 */
const TeamCodesDisplay: React.FC<TeamCodesDisplayProps> = ({
    coachCode,
    athleteCode,
    teamName,
    organizationName
}) => {
    const [copied, setCopied] = useState<string | null>(null);
    const { isDark } = useTheme();
    
    /**
     * Copy code to clipboard with visual feedback
     * @param code - The code to copy
     * @param type - Type of code ('coach' or 'athlete') for feedback state
     */
    const copyToClipboard = (code: string, type: string) => {
        navigator.clipboard.writeText(code);
        setCopied(type);
        setTimeout(() => setCopied(null), 2000);
    };
    
    return (
        <div className="team-codes-success cgm-card p-6 space-y-6">
            {/* Success Icon */}
            <div className="success-icon mb-4 flex justify-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                    <svg 
                        className="w-10 h-10 text-emerald-500" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M5 13l4 4L19 7" 
                        />
                    </svg>
                </div>
            </div>
            
            {/* Success Message */}
            <div className="text-center">
                <h2 className="text-2xl font-bold cgm-text-primary mb-2">
                    Team Created Successfully!
                </h2>
                <p className="cgm-text-secondary text-base">
                    {teamName}
                </p>
                <p className="cgm-text-muted text-sm">
                    {organizationName}
                </p>
            </div>
            
            {/* Team Codes Container */}
            <div className="codes-container space-y-4">
                <p className="cgm-text-secondary text-sm text-center">
                    Share these codes to invite members to your team:
                </p>
                
                {/* Coach Code Card */}
                <div className="code-card cgm-card-secondary p-4 space-y-3">
                    <label className="text-sm font-medium cgm-text-primary">
                        Coach Invite Code
                    </label>
                    <div className="flex items-center gap-2">
                        <code className={`flex-1 px-4 py-3 rounded-lg font-mono text-lg font-semibold text-center
                            ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            {coachCode}
                        </code>
                        <button
                            onClick={() => copyToClipboard(coachCode, 'coach')}
                            className="cgm-button-secondary px-4 py-3 whitespace-nowrap transition-all duration-200"
                        >
                            {copied === 'coach' ? (
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Copied!
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    Copy
                                </span>
                            )}
                        </button>
                    </div>
                    <p className="text-xs cgm-text-muted">
                        Share this code with other coaches who need to manage the team
                    </p>
                </div>
                
                {/* Athlete Code Card */}
                <div className="code-card cgm-card-secondary p-4 space-y-3">
                    <label className="text-sm font-medium cgm-text-primary">
                        Athlete Invite Code
                    </label>
                    <div className="flex items-center gap-2">
                        <code className={`flex-1 px-4 py-3 rounded-lg font-mono text-lg font-semibold text-center
                            ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            {athleteCode}
                        </code>
                        <button
                            onClick={() => copyToClipboard(athleteCode, 'athlete')}
                            className="cgm-button-secondary px-4 py-3 whitespace-nowrap transition-all duration-200"
                        >
                            {copied === 'athlete' ? (
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Copied!
                                </span>
                            ) : (
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    Copy
                                </span>
                            )}
                        </button>
                    </div>
                    <p className="text-xs cgm-text-muted">
                        Share this code with athletes to join your team
                    </p>
                </div>
            </div>
            
            {/* Call to Action */}
            <div className="mt-6 space-y-3">
                <p className="text-sm cgm-text-secondary text-center">
                    You can access these codes anytime from your team settings
                </p>
                <Link 
                    to="/login" 
                    className="cgm-button w-full py-3 px-4 flex items-center justify-center"
                >
                    Continue to Login
                </Link>
            </div>
        </div>
    );
};

export default TeamCodesDisplay;

